import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import api from '../../services/api';

function normalizeConflictName(value) {
  return String(value || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

const emptyConflicts = () => ({ events: {}, tasks: {}, armados: {}, operations: {} });

// Centraliza la fuente de verdad mensual de Agenda. Las acciones de cada fila
// siguen viviendo en AgendaPage; este hook sólo carga, conserva y sincroniza datos.
export default function useAgendaData({ month, fallbackData, includeArchived = false }) {
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [armados, setArmados] = useState([]);
  const [operations, setOperations] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [lugares, setLugares] = useState([]);
  const [clients, setClients] = useState([]);
  const [salesRefs, setSalesRefs] = useState([]);
  const [assignmentModel, setAssignmentModel] = useState(null);
  const [assignmentModelError, setAssignmentModelError] = useState(false);
  const [assignmentModelLoading, setAssignmentModelLoading] = useState(false);
  const [monthConflicts, setMonthConflicts] = useState(emptyConflicts);
  const [loading, setLoading] = useState(true);

  const monthCache = useRef({});
  const monthRef = useRef(month);
  const loadRef = useRef(null);
  // Guarda de orden: con navegación rápida entre meses o un reload silencioso
  // superpuesto a uno manual, puede haber dos fetchAssignmentModel() en vuelo
  // a la vez. Sin esto, una respuesta vieja que resuelve después que la nueva
  // pisaría el resultado correcto (éxito viejo tapa un error nuevo, o
  // viceversa). Sólo aplica el resultado si sigue siendo el pedido más reciente.
  const assignmentModelSeq = useRef(0);

  // El modelo de asignación (fases/labels/roles) no es propio de un mes: se
  // maneja aparte del resto del batch para que un fallo puntual de esta ruta
  // no dependa del resultado de /events (que sí puede tirar el Promise.all
  // entero) y para poder reintentarlo solo, sin recargar toda la agenda.
  const fetchAssignmentModel = useCallback(async () => {
    const seq = ++assignmentModelSeq.current;
    setAssignmentModelLoading(true);
    try {
      const { data } = await api.get('/staff-assignments/model');
      if (seq !== assignmentModelSeq.current) return data;
      setAssignmentModel(data);
      setAssignmentModelError(false);
      return data;
    } catch (error) {
      console.error(error);
      if (seq !== assignmentModelSeq.current) return null;
      setAssignmentModel(null);
      setAssignmentModelError(true);
      return null;
    } finally {
      if (seq === assignmentModelSeq.current) setAssignmentModelLoading(false);
    }
  }, []);

  const applyMonthData = useCallback((data) => {
    setEvents(data.events);
    setTasks(data.tasks);
    setArmados(data.armados);
    setOperations(data.operations || []);
    setPersonnel(data.personnel);
    setLugares(data.lugares);
    if (data.clients) setClients(data.clients);
    if (data.salesRefs) setSalesRefs(data.salesRefs);
    setMonthConflicts(data.conflicts);
  }, []);

  const load = useCallback(async (ym, { silent = false } = {}) => {
    monthRef.current = ym;
    // La caché por mes no puede compartirse entre vistas con/sin archivados:
    // son dos respuestas distintas del servidor para el mismo rango de fechas.
    const cacheKey = includeArchived ? `${ym}::archived` : ym;
    const cached = monthCache.current[cacheKey];
    if (cached && !silent) {
      applyMonthData(cached);
    } else if (!cached && !silent) {
      setLoading(true);
    }

    const [year, rawMonth] = ym.split('-').map(Number);
    const from = `${year}-${String(rawMonth).padStart(2, '0')}-01`;
    const lastDay = new Date(year, rawMonth, 0).getDate();
    const to = `${year}-${String(rawMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    const eventsQuery = `/events?from=${from}&to=${to}${includeArchived ? '&archived=all' : ''}`;

    try {
      const [eventsRes, tasksRes, armadosRes, operationsRes, personnelRes, conflictsRes, lugaresRes, clientsRes, salesRefsRes] = await Promise.all([
        api.get(eventsQuery),
        api.get(`/tasks?from=${from}&to=${to}`),
        api.get(`/armados?from=${from}&to=${to}`),
        api.get(`/event-operations?from=${from}&to=${to}`).catch(() => ({ data: [] })),
        api.get('/personnel/agenda-directory'),
        api.get('/staff-assignments/conflicts-month', { params: { from, to } }).catch(() => ({ data: [] })),
        api.get('/lugares').catch(() => ({ data: [] })),
        api.get('/clients').catch(() => ({ data: [] })),
        api.get('/auth/sales-refs').catch(() => ({ data: [] })),
        fetchAssignmentModel(),
      ]);

      const conflicts = emptyConflicts();
      for (const conflict of conflictsRes.data) {
        const key = `${conflict.phase}::${normalizeConflictName(conflict.person_name)}`;
        if (conflict.event_id) {
          conflicts.events[conflict.event_id] = conflicts.events[conflict.event_id] || new Set();
          conflicts.events[conflict.event_id].add(key);
        }
        if (conflict.task_id) {
          conflicts.tasks[conflict.task_id] = conflicts.tasks[conflict.task_id] || new Set();
          conflicts.tasks[conflict.task_id].add(key);
        }
        if (conflict.armado_id) {
          conflicts.armados[conflict.armado_id] = conflicts.armados[conflict.armado_id] || new Set();
          conflicts.armados[conflict.armado_id].add(key);
        }
        if (conflict.operation_id) {
          conflicts.operations[conflict.operation_id] = conflicts.operations[conflict.operation_id] || new Set();
          conflicts.operations[conflict.operation_id].add(key);
        }
      }

      const data = {
        events: eventsRes.data,
        tasks: tasksRes.data,
        armados: armadosRes.data,
        operations: operationsRes.data,
        personnel: personnelRes.data,
        lugares: Array.isArray(lugaresRes.data) ? lugaresRes.data : [],
        clients: Array.isArray(clientsRes.data) ? clientsRes.data : [],
        salesRefs: Array.isArray(salesRefsRes.data) ? salesRefsRes.data : [],
        conflicts,
      };
      monthCache.current[cacheKey] = data;
      if (ym === monthRef.current) applyMonthData(data);
    } catch (error) {
      console.error(error);
      if (process.env.NODE_ENV === 'development' && !silent && fallbackData) {
        setEvents(fallbackData.events || []);
        setTasks(fallbackData.tasks || []);
        setArmados(fallbackData.armados || []);
        setOperations(fallbackData.operations || []);
        setMonthConflicts(emptyConflicts());
      }
    } finally {
      setLoading(false);
    }
  }, [applyMonthData, fallbackData, fetchAssignmentModel, includeArchived]);

  useEffect(() => { load(month); }, [load, month]);

  loadRef.current = () => load(month, { silent: true });
  useEffect(() => {
    const socket = io('/', { withCredentials: true });
    let timer = null;
    const reload = () => {
      clearTimeout(timer);
      timer = setTimeout(() => loadRef.current?.(), 400);
    };
    socket.on('agenda:changed', reload);
    socket.on('personnel:updated', reload);
    return () => { clearTimeout(timer); socket.disconnect(); };
  }, []);

  return {
    events, setEvents,
    tasks, setTasks,
    armados, setArmados,
    operations, setOperations,
    personnel, setPersonnel,
    lugares, setLugares,
    clients, setClients,
    salesRefs, setSalesRefs,
    assignmentModel, setAssignmentModel,
    assignmentModelError,
    assignmentModelLoading,
    retryAssignmentModel: fetchAssignmentModel,
    monthConflicts, setMonthConflicts,
    loading,
    load,
  };
}
